/* eslint-disable import/prefer-default-export */
/* eslint-disable max-len */
import React from 'react';
import styled from 'styled-components';
import Icon from 'antd/lib/icon';
import Checkbox from 'antd/lib/checkbox';
import Popover from 'antd/lib/popover';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';

import { ColumnDownloadReport } from './actions';
import { useReportSelection } from './ReportSelectionContext';

const PreviewPopover = styled.div`
  max-width: 350px;

  .preview-header {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }

  .preview-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 12px;

    .label {
      color: #8c8c8c;
    }

    .value {
      font-weight: 500;
    }
  }

  .preview-devices {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;

    .devices-title {
      font-weight: 500;
      font-size: 12px;
      margin-bottom: 4px;
    }

    .device-item {
      font-size: 11px;
      color: #595959;
      padding: 2px 0;
    }
  }
`;

// Quick preview content component
function ReportPreviewContent({ record }) {
  const devices = record.devices || [];

  return (
    <PreviewPopover>
      <div className="preview-header">{record.name}</div>
      <div className="preview-row">
        <span className="label">Kupac:</span>
        <span className="value">{record.customer?.name || '-'}</span>
      </div>
      <div className="preview-row">
        <span className="label">Napravljeno:</span>
        <span className="value">{moment(record.createdAt).format('DD.MM.YYYY.')}</span>
      </div>
      <div className="preview-row">
        <span className="label">Broj aparata:</span>
        <span className="value">{devices.length}</span>
      </div>
      {devices.length > 0 && (
        <div className="preview-devices">
          <div className="devices-title">Aparati:</div>
          {devices.slice(0, 5).map((d, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="device-item">
              {d.device?.code || '-'}
{' '}
(
{d.device?.kind || '-'}
) -
{d.serialNo || 'bez s/n'}
            </div>
          ))}
          {devices.length > 5 && (
            <div className="device-item" style={{ fontStyle: 'italic' }}>
              ... i još
              {' '}
              {devices.length - 5}
              {' '}
              aparata
            </div>
          )}
        </div>
      )}
    </PreviewPopover>
  );
}

// Download column with preview icon
export function ColumnDownloadWithPreview({ record }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Popover
        content={<ReportPreviewContent record={record} />}
        trigger="click"
        placement="left"
      >
        <Icon
          type="eye"
          style={{ cursor: 'pointer', fontSize: '14px', color: '#8c8c8c' }}
        />
      </Popover>
      <ColumnDownloadReport record={record} />
    </div>
  );
}

const CustomerPopoverContent = styled.div`
  .customer-name {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 8px;
  }

  .customer-details {
    font-size: 12px;
    color: #595959;
    margin-bottom: 8px;
  }

  .customer-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #1890ff;
    font-size: 12px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Customer column with popover link
export function ColumnCustomerWithLink({ record }) {
  const { customer } = record;

  if (!customer) {
    return <span>-</span>;
  }

  const popoverContent = (
    <CustomerPopoverContent>
      <div className="customer-name">{customer.name}</div>
      {customer.code && (
        <div className="customer-details">
          Šifra:
          {' '}
          {customer.code}
        </div>
      )}
      <Link to={`/customers/${customer._id}`}>
        <Icon type="user" />
        {' '}
        Pogledaj Kupca
      </Link>
    </CustomerPopoverContent>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="hover"
      placement="right"
      mouseEnterDelay={0.3}
    >
      <span style={{ cursor: 'pointer' }}>{customer.name}</span>
    </Popover>
  );
}

// Checkbox column for report selection
export function ColumnReportCheckbox({ record }) {
  const { isSelected, toggleReport } = useReportSelection();

  const handleChange = (e) => {
    e.stopPropagation();
    toggleReport(record);
  };

  return (
    <Checkbox
      checked={isSelected(record._id)}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// Header checkbox for select all
export function HeaderSelectAllCheckbox({ data }) {
  const { selectedReports, selectAll, clearSelection } = useReportSelection();
  const reports = data?.data || [];

  const allSelected = reports.length > 0 && reports.every(
    (r) => selectedReports.some((sr) => sr._id === r._id)
  );
  const someSelected = reports.some(
    (r) => selectedReports.some((sr) => sr._id === r._id)
  );

  const handleChange = () => {
    if (allSelected) {
      clearSelection();
    } else {
      selectAll(reports);
    }
  };

  return (
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected && !allSelected}
      onChange={handleChange}
    />
  );
}
